import { useCallback, useEffect, useState } from "react";
import { Badge, BlockStack, Button, Card, InlineStack, Text } from "@shopify/polaris";
import { ClipboardIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { money, rpcError } from "../../utility/egresos";

// A tercero's CLABE for sending money BACK to us (anticipo change, an
// overpayment, a deposit). Not created by default: only when someone with
// services.pay asks for it here. It lives under the Egresos Fintoc account and
// whatever arrives is applied to this tercero's egreso by pagos-svc; when it
// cannot tell which egreso, the payment waits here for "Aplicar".

const formatClabe = (c) => (c || "").replace(/(\d{4})(?=\d)/g, "$1 ").trim();

// Ready to paste into WhatsApp: bare digits (banking apps reject spaces),
// asterisks are WhatsApp bold.
function returnMessage(clabe, name, owedCents) {
  return [
    "*Datos para devolución*",
    "",
    "Nombre: *GOMOVE*",
    "Banco: Fintoc",
    `CLABE: *${clabe}*`,
    owedCents > 0 ? `Monto: *${money(owedCents)}*` : null,
    "",
    `Esta CLABE es exclusiva de ${name}. Lo que envíes aquí se registra automáticamente.`,
  ]
    .filter((l) => l !== null)
    .join("\n");
}

export default function ReturnClabeCard({ egreso, balance, payeeName, canPay, onApplied }) {
  const payeeId = egreso?.payee_type === "payee" ? egreso.payee_id : null;
  const owed = Number(balance?.por_comprobar_cents || 0) + Number(balance?.deposito_cents || 0);
  const [row, setRow] = useState(undefined);
  const [waiting, setWaiting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    if (!payeeId) return;
    const [{ data: c }, { data: pays }] = await Promise.all([
      supabase
        .from("payment_clabes")
        .select("clabe, status")
        .eq("owner_type", "payee")
        .eq("owner_id", payeeId)
        .in("status", ["pending", "active"])
        .maybeSingle(),
      supabase
        .from("payments")
        .select("id, amount_cents, occurred_at")
        .eq("payer_type", "payee")
        .eq("payer_id", payeeId)
        .eq("direction", "in")
        .order("occurred_at"),
    ]);
    setRow(c || null);
    const ids = (pays || []).map((p) => p.id);
    if (!ids.length) return setWaiting([]);
    const { data: bals } = await supabase.from("payment_balances").select("payment_id, unapplied_cents").in("payment_id", ids);
    const left = Object.fromEntries((bals || []).map((b) => [b.payment_id, Number(b.unapplied_cents)]));
    setWaiting((pays || []).filter((p) => left[p.id] > 0).map((p) => ({ ...p, unapplied_cents: left[p.id] })));
  }, [payeeId]);

  useEffect(() => {
    load();
  }, [load]);

  // Fintoc answers in a few seconds; poll while the CLABE is being created.
  useEffect(() => {
    if (row?.status !== "pending") return undefined;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [row, load]);

  if (!payeeId || row === undefined) return null;
  // Nothing to get back and no CLABE yet: nothing to show.
  if (!row && owed <= 0 && !waiting.length) return null;

  const create = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("request_payment_clabe", { p_owner_type: "payee", p_owner_id: payeeId });
    setBusy(false);
    if (err) return setError(rpcError(err));
    load();
  };

  const apply = async (paymentId) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("apply_payee_return", { p_payment: paymentId, p_egreso: egreso.id });
    setBusy(false);
    if (err) return setError(rpcError(err));
    load();
    onApplied?.();
  };

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard unavailable — the CLABE is on screen */
    }
  };

  return (
    <Card>
      <BlockStack gap="200">
        <Text variant="headingSm">CLABE para devoluciones</Text>
        {!row ? (
          canPay ? (
            <InlineStack>
              <Button size="slim" loading={busy} onClick={create}>
                Crear CLABE
              </Button>
            </InlineStack>
          ) : (
            <Text tone="subdued">Sin CLABE</Text>
          )
        ) : row.status !== "active" || !row.clabe ? (
          <InlineStack>
            <Badge tone="attention" progress="incomplete">Generando…</Badge>
          </InlineStack>
        ) : (
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <button
              type="button"
              onClick={() => copy("clabe", row.clabe)}
              title="Clic para copiar la CLABE"
              style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Text variant="headingMd" as="span" numeric>
                {formatClabe(row.clabe)}
              </Text>
              {copied === "clabe" && (
                <Text tone="success" variant="bodySm" as="span">
                  Copiada
                </Text>
              )}
            </button>
            <Button variant="plain" icon={ClipboardIcon} onClick={() => copy("message", returnMessage(row.clabe, payeeName || "este tercero", owed))}>
              {copied === "message" ? "Copiado" : "Mensaje"}
            </Button>
          </InlineStack>
        )}
        {waiting.map((p) => (
          <InlineStack key={p.id} align="space-between" blockAlign="center" gap="200" wrap={false}>
            <Text>
              Recibimos {money(p.unapplied_cents)}{" "}
              <Text as="span" tone="subdued">
                · {new Date(p.occurred_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
              </Text>
            </Text>
            {canPay && owed > 0 && !egreso.closed_at && (
              <Button size="slim" loading={busy} onClick={() => apply(p.id)}>
                Aplicar
              </Button>
            )}
          </InlineStack>
        ))}
        {error && <Text tone="critical">{error}</Text>}
      </BlockStack>
    </Card>
  );
}
